import "back/wsp/actions/actions_Perms";
import {IWspUiEnv} from "lib/wsp/wsp";
import {POPUP} from "back/commons/widgets/popups";
import {Action, ActionMenu} from "lib/commons/actions";
import {ISearchPersist, ISearchViewResult, SearchExportColumn} from "lib/wsp/search";
import {IRegPointer} from "lib/commons/registry";
import {IO} from "lib/commons/io/io";
import {DOM, JSX} from "lib/commons/xml/dom";
import {OSearchExportCsvInit, SearchExportCsv} from "back/wsp/dialogs/searchExportCsv";
import {BASIS} from "back/commons/basis";


/** Exporter la liste des items (scar) */
export class ExportResultItems<C extends ISearchViewResult & IRegPointer<IWspUiEnv>> extends Action<C> {

	constructor() {
		super("exportResultItems");
		this._label = "Exporter les items trouvés (.scwsp)";
		this._description = "Créer une archive avec les items trouvés";
		this._icon = "/@skin@/wsp/dialogs/searchItems/exports.svg";
		this._group = 'io';
		this.requireEnabledPerm('action.searches#export.items');
	}

	async execute(ctx: C, ev?: Event) {
		const wsp = ctx.reg.env.wsp;
		const name = IO.getValidFileName(wsp.wspTitle, ".scwsp", 'date');
		const where = ctx.buildWhereXml();
		const url = ctx.reg.env.universe.config.wsps.searchUrl.resolve(IO.qs("cdaction", "Search", "param", wsp.code, "format", 'scwsp', "downloadFileName", name));
		IO.saveRespAs(url, ctx.reg.env.uiRoot, new Map().set("request", DOM.serializer().serializeToString(where)), "application/scenari.scwsp", "POST");
	}

}

/** Exporter la liste des propriétés des items (csv) */
export class ExportResultCsv_item<C extends ISearchViewResult & IRegPointer<IWspUiEnv>> extends Action<C> {

	constructor() {
		super("exportResultCsv-item");
		this._label = "Exporter les propriétés des items trouvés (.csv)";
		this._description = "Créer un CSV avec les propriétés des items trouvés";
		this._icon = "/@skin@/wsp/dialogs/searchItems/exports.svg";
		this._group = 'io';
		this.requireEnabledPerm('action.searches#export.props');
	}

	async execute(ctx: C, ev?: Event) {
		const {SearchExportCsv} = await import("back/wsp/dialogs/searchExportCsv.js");
		const initHeader: OSearchExportCsvInit = BASIS.newInit({
			reg: ctx.reg,
			privateUri: "/itemSearchesColsCsv.json",
			bibConfsLists: ["confcsv:searchExportCsv:bib", "confcsv:searchExportCsv:bib:item"],
			buildWhereXmlFct: (ctxfct) => {return ctx.buildWhereXml()},
			appHeaderMainActionsLists: [SearchExportCsv.APPBAR_MAIN_ACTIONS, "actions:searchExportCsv:bar:item"],
			columns: await Promise.all(ctx.reg.mergeLists<Promise<SearchExportColumn<C>>>("columns:wspApp:searches-export", "columns:wspApp:searches-export:item")),
		} as OSearchExportCsvInit, ctx.reg);
		return POPUP.showDialog<void>(new SearchExportCsv().initialize(initHeader), ctx.reg.env.uiRoot, {titleBar: {barLabel: {label: "Export CSV de la recherche"}}, fixSize: false, resizer: {}}).onNextClose();
	}

}

/** Exporter la liste des propriétés des taches (csv) */
export class ExportResultCsv_task<C extends ISearchViewResult & IRegPointer<IWspUiEnv>> extends Action<C> {

	constructor() {
		super("exportResultCsv-task");
		this._label = "Exporter les propriétés des tâches trouvées (.csv)";
		this._description = "Créer un CSV avec les propriétés des tâches trouvées";
		this._icon = "/@skin@/wsp/dialogs/searchItems/expCsv.svg";
		this._group = 'io';
		this.requireEnabledPerm('action.searches#export.props');
	}

	async execute(ctx: C, ev?: Event) {
		const {SearchExportCsv} = await import("back/wsp/dialogs/searchExportCsv.js");
		const initHeader: OSearchExportCsvInit = BASIS.newInit({
			reg: ctx.reg,
			privateUri: "/taskSearchesColsCsv.json",
			bibConfsLists: ["confcsv:searchExportCsv:bib", "confcsv:searchExportCsv:bib:task"],
			buildWhereXmlFct: (ctxfct) => {return ctx.buildWhereXml()},
			appHeaderMainActionsLists: [SearchExportCsv.APPBAR_MAIN_ACTIONS, "actions:searchExportCsv:bar:task"],
			columns: await Promise.all(ctx.reg.mergeLists<Promise<SearchExportColumn<C>>>("columns:wspApp:searches-export", "columns:wspApp:searches-export:task")),
		} as OSearchExportCsvInit, ctx.reg);
		return POPUP.showDialog<void>(new SearchExportCsv().initialize(initHeader), ctx.reg.env.uiRoot, {titleBar: {barLabel: {label: "Export CSV de la recherche"}}, fixSize: false, resizer: {}}).onNextClose();
	}

}

/** Afficher / ouvrir les requêtes pré-enregistrées */
export class TplRequestsMenu<C extends IRegPointer<IWspUiEnv> & ISearchViewResult> extends ActionMenu<C> {
	constructor(acts: TplRequestAction<C>[]) {
		super('tplRequestsMenu');
		this._label = "Bibliothèque de requêtes";
		this._group = "lib";
		this._actions = acts;
		this._icon = "/@skin@/wsp/dialogs/searchItems/bibRequests.svg";
	}

	isVisible(ctx: C): boolean {
		if (!this._actions.length) return false;
		return super.isVisible(ctx);
	}

	/** Construction d'un TplRequestsMenu à partir de listes de {@link ISearchPersist} */
	static makeFromSearchPersistLists(ctx: IRegPointer<IWspUiEnv>, ...lists: string[]): TplRequestsMenu<IRegPointer<IWspUiEnv> & ISearchViewResult> {
		const elts = ctx.reg.mergeLists(...lists) as ISearchPersist[];
		const actions: TplRequestAction<IRegPointer<IWspUiEnv> & ISearchViewResult>[] = [];
		elts.forEach(entry => actions.push(new TplRequestAction(entry.id, entry.title, entry.whereCrit, entry.description)));
		return new TplRequestsMenu(actions);
	}
}

export class TplRequestAction<C extends IRegPointer<IWspUiEnv> & ISearchViewResult> extends Action<C> {
	private _searchId: string;

	constructor(id: string, public title: string, public whereXml: Element | ((ctx: IRegPointer<IWspUiEnv>) => Element), public description?: string) {
		super(id);
		this._searchId = id;
		this._label = title;
		this._description = description;
	}

	async execute(ctx: C, ev?: Event): Promise<any | 'noPreventDefault'> {
		const where: Element = this.whereXml instanceof Element ? this.whereXml : this.whereXml(ctx);
		return this._searchId = await ctx.viewSearch(where, this._searchId, this.getLabel(ctx), this.getDescription(ctx));
	}
}
